import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonComponent } from '../../button/button.component';
import { InputComponent } from '../../input/input.component';
import { SelectComponent } from '../../select/select.component';
import { formatDateForInput } from '../../../utils/date-utils';
import { dateRangeValidator } from '../../../utils/date-range.validator';
import { HighSchoolEducationExtended } from '../../../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { CvOptions } from '../../../models/cv-options.model';
import { AttachmentService } from '../../../../../../../libs/shared/src/lib/services/attachment.service';
import { FIELD_VALIDATION } from '../../../../../../../libs/shared/src/lib/constants/validation-constants';
import { catchError, finalize, map, of } from 'rxjs';
import { FileUploadService } from '../../../../../../../libs/shared/src/lib/services/file-upload/file-upload.service';
import { AttachmentData } from '../../../../../../../libs/shared/src/lib/services/file-upload/file-upload-types';

@Component({
  selector: 'app-high-school-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, InputComponent, SelectComponent, TranslocoPipe],
  templateUrl: './high-school-form.component.html',
  styleUrls: ['./high-school-form.component.scss'],
})
export class HighSchoolFormComponent implements OnInit, OnChanges {
  isCurrentlyStudying = false;

  @Input() editingHighSchool: HighSchoolEducationExtended | null = null;
  @Input() editingIndex: number | null = null;
  @Input() cvOptions: CvOptions | null = null;
  @Input() isAuthenticated = false;

  @Output() save = new EventEmitter<Partial<HighSchoolEducationExtended>>();
  @Output() cancel = new EventEmitter<void>();

  highSchoolForm: FormGroup;
  isUploading = false;
  fileError: string | null = null;
  educationLevelOptions: { value: number; label: string }[] = [];

  // Expose validation constants to template
  readonly VALIDATION = FIELD_VALIDATION;

  // Single attachment object to handle all file-related data
  attachment: AttachmentData = {
    file: undefined,
    fileName: '',
    fileSize: '',
    fileType: ''
  };

  // List for duplicate checking
  existingFiles: AttachmentData[] = [];

  constructor(
    private fb: FormBuilder,
    private attachmentService: AttachmentService,
    private fileUploadService: FileUploadService,
    private translocoService: TranslocoService
  ) {
    this.highSchoolForm = this.createForm();
  }

  ngOnInit(): void {
    this.buildEducationLevelOptions();
    if (this.editingHighSchool) {
      this.populateForm(this.editingHighSchool);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cvOptions']) {
      this.buildEducationLevelOptions();
    }
    if (changes['editingHighSchool'] && changes['editingHighSchool'].currentValue) {
      this.populateForm(changes['editingHighSchool'].currentValue);
    }
  }

  private buildEducationLevelOptions(): void {
    this.educationLevelOptions = (this.cvOptions?.educationLevels || [])
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map(level => ({
        value: level.id,
        label: this.translocoService.translate('jobEducationLevels.' + level.educationKey)
      }));
  }

  /**
   * Create the form with validation using SSOT validation constants
   * @returns FormGroup
   */
  private createForm(): FormGroup {
    return this.fb.group(
      {
        schoolName: ['', [Validators.required, Validators.maxLength(FIELD_VALIDATION.HIGH_SCHOOL_NAME_MAX_LENGTH)]],
        fieldOfStudy: ['', [Validators.required, Validators.maxLength(FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH)]],
        educationLevelID: ['', Validators.required],
        city: ['', [Validators.maxLength(FIELD_VALIDATION.CITY_MAX_LENGTH)]],
        country: ['', [Validators.maxLength(FIELD_VALIDATION.COUNTRY_MAX_LENGTH)]],
        startDate: ['', [Validators.required]],
        endDate: ['', [Validators.required]],
        isCurrentlyStudying: [false],
        attachmentId: [''], // For authenticated users
        // TODO: Add URL field for non-authenticated user uploads
        attachmentUrl: [''], // For non-authenticated users
      },
      { validators: dateRangeValidator({ ongoingKey: 'isCurrentlyStudying' }) }
    );
  }

  private populateForm(highSchool: HighSchoolEducationExtended): void {
    // Use either highSchoolName or schoolName (for backward compatibility)
    const schoolName = highSchool.highSchoolName || '';

    // Format dates for the date inputs (YYYY-MM-DD format)
    let formattedStartDate = '';
    let formattedEndDate = '';

    // Format dates using centralized utility functions
    if (highSchool.startDate) {
      formattedStartDate = formatDateForInput(highSchool.startDate);
    }

    if (highSchool.endDate) {
      formattedEndDate = formatDateForInput(highSchool.endDate);
    }

    // Reset attachment object
    this.attachment = {
      file: undefined,
      fileName: '',
      fileSize: '',
      fileType: ''
    };

    console.log(highSchool);


    // Handle attachment from backend structure
    let attachmentId = '';
    if (highSchool.attachmentID && highSchool.candidateAttachment) {
      attachmentId = highSchool.attachmentID.toString();
      this.attachment.fileName = highSchool.candidateAttachment.fileName || '';
      this.attachment.fileType = highSchool.candidateAttachment.fileType || '';

      // Set file size if available from backend
      if (highSchool.candidateAttachment.fileSize) {
        const size = highSchool.candidateAttachment.fileSize;
        this.attachment.fileSize = typeof size === 'number' ? this.formatFileSize(size) : size.toString();
      }
    }

    this.highSchoolForm.patchValue({
      schoolName: schoolName,
      fieldOfStudy: highSchool.fieldOfStudy,
      educationLevelID: highSchool.educationLevelID || '',
      city: highSchool.city,
      country: highSchool.country,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      attachmentId: attachmentId,
    });
  }

  /**
   * Display file error message
   * @param message Error message to display
   */
  private showFileError(message: string): void {
    this.fileError = message;
    // Reset file data
    this.attachment = {
      file: undefined,
      fileName: '',
      fileSize: '',
      fileType: ''
    };
    // Reset form attachment fields
    this.highSchoolForm.patchValue({
      attachmentId: null,
      attachmentUrl: null,
    });
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];

      // Get education upload configuration
      const config = this.fileUploadService.getDefaultConfig('education');

      // Validate file using unified service
      const validation = this.fileUploadService.validateFile(file, config);
      if (!validation.valid) {
        this.showFileError(validation.error || this.translocoService.translate('profile.fileUpload.errors.invalidFile'));
        return;
      }

      // Check for duplicates
      if (this.fileUploadService.checkDuplicateFile(file, this.existingFiles)) {
        this.showFileError(this.translocoService.translate('profile.fileUpload.errors.duplicate'));
        return;
      }

      // Store file information using unified service
      const fileInfo = this.fileUploadService.extractFileInfo(file);
      this.attachment = {
        file: file,
        fileName: fileInfo.fileName,
        fileSize: fileInfo.fileSize,
        fileType: fileInfo.fileType
      };
      this.fileError = null;

      // Process the file for upload
      this.processFile();
    }
  }

  /**
   * Process the file for upload using unified service
   */
  async processFile(): Promise<void> {
    if (!this.attachment.file) {
      return;
    }

    this.isUploading = true;
    this.fileError = null;

    try {
      // Convert file to blob using unified service
      const blob = await this.fileUploadService.fileToBlob(this.attachment.file);

      // Use unified service to perform upload
      const uploadObservable = this.fileUploadService.performUpload(
        blob,
        this.attachment.file,
        this.isAuthenticated,
        this.attachmentService
      );

      uploadObservable
        .pipe(
          finalize(() => {
            this.isUploading = false;
          })
        )
        .subscribe({
          next: (response) => {
            const result = this.fileUploadService.handleUploadResponse(response, this.attachment.file!, this.isAuthenticated);

            if (result.success && result.data) {
              if (result.isUrl) {
                // Non-authenticated upload - store URL
                this.highSchoolForm.patchValue({
                  attachmentUrl: result.data.url,
                  attachmentId: null
                });
              } else {
                // Authenticated upload - store ID
                this.highSchoolForm.patchValue({
                  attachmentId: result.data.id,
                  attachmentUrl: null
                });
              }

              // Add to existing files list for duplicate checking
              this.existingFiles.push(this.attachment);
            } else {
              this.showFileError(result.error || this.translocoService.translate('profile.fileUpload.errors.uploadFailed'));
            }
          },
          error: (error: any) => {
            let errorMessage = this.translocoService.translate('profile.fileUpload.errors.uploadError');

            if (error.status === 413) {
              errorMessage = this.translocoService.translate('profile.fileUpload.errors.tooLarge');
            } else if (error.status === 415) {
              errorMessage = this.translocoService.translate('profile.fileUpload.errors.unsupportedType');
            } else if (error.status === 0) {
              errorMessage = this.translocoService.translate('profile.fileUpload.errors.networkError');
            } else if (error.error?.message) {
              errorMessage = error.error.message;
            }

            this.showFileError(errorMessage);
            console.error('Error uploading file:', error);
          }
        });
    } catch (error) {
      this.isUploading = false;
      this.showFileError(this.translocoService.translate('profile.fileUpload.errors.processingError'));
      console.error('Error processing file:', error);
    }
  }

  /**
   * Format file size in bytes to a human-readable format
   * @param bytes File size in bytes
   * @returns Formatted file size string (e.g., "2.5 MB")
   */
  formatFileSize(bytes: number): string {
    return this.fileUploadService.formatFileSize(bytes);
  }

  /**
   * Remove the uploaded file
   */
  removeFile(): void {
    this.attachment = {
      file: undefined,
      fileName: '',
      fileSize: '',
      fileType: ''
    };
    this.fileError = null;
    this.highSchoolForm.patchValue({
      attachmentId: null,
      attachmentUrl: null
    });
  }

  /**
   * Submit the form
   */
  onSubmit(): void {
    if (this.highSchoolForm.invalid) {
      this.highSchoolForm.markAllAsTouched();
      return;
    }

    if (this.isUploading) {
      // Wait for upload to complete
      return;
    }

    const formValue = this.highSchoolForm.getRawValue();

    // Create the high school education object
    const highSchool: Partial<HighSchoolEducationExtended> = {
      id: this.editingHighSchool?.id,
      highSchoolName: formValue.schoolName,
      fieldOfStudy: formValue.fieldOfStudy,
      educationLevelID: formValue.educationLevelID,
      city: formValue.city,
      country: formValue.country,
      startDate: formValue.startDate,
      endDate: this.isCurrentlyStudying ? null : formValue.endDate,
    };

    // Handle attachment for both auth types using unified service approach
    if (formValue.attachmentId) {
      // Authenticated user - has real attachment ID
      highSchool.attachmentID = parseInt(formValue.attachmentId, 10);
      highSchool.candidateAttachment = this.fileUploadService.createAttachmentObject(
        this.attachment.file || new File([], this.attachment.fileName || ''),
        { id: parseInt(formValue.attachmentId, 10) },
        true
      );
    } else if (formValue.attachmentUrl) {
      // Non-authenticated user - has URL from upload
      highSchool.candidateAttachment = this.fileUploadService.createAttachmentObject(
        this.attachment.file || new File([], this.attachment.fileName || ''),
        formValue.attachmentUrl,
        false
      );
    }

    this.save.emit(highSchool);
  }
}
